import { z } from 'zod';
import { currencyCodeSchema, idSchema, isoDateSchema, isoDateTimeSchema, moneySchema, } from './common.js';
import { compareDecimal } from '../utils/decimal.js';
import { SUPPORTED_LOCALES } from '../constants/app.js';
/** Business dates must survive a UTC round trip, including leap-year validation. */
export const ledgerDateSchema = isoDateSchema.refine((value) => {
    const date = new Date(`${value}T00:00:00.000Z`);
    return Number.isFinite(date.getTime()) && date.toISOString().slice(0, 10) === value;
}, 'Invalid calendar date');
export const ledgerAccountKindSchema = z.enum([
    'ASSET',
    'LIABILITY',
    'EQUITY',
    'REVENUE',
    'EXPENSE',
]);
export const ledgerAccountRoleSchema = z.enum([
    'GENERAL',
    'RECEIVABLE',
    'PAYABLE',
    'BANK',
    'CASH',
    'CLEARING',
    'OUTPUT_TAX',
    'INPUT_TAX',
    'DEFERRED_TAX',
    'DISCOUNT',
    'FEE',
    'FX',
    'OPENING',
    'RETAINED_EARNINGS',
    'ADVANCE_RECEIVED',
    'ADVANCE_PAID',
    'TRANSFER_CLEARING',
    'BAD_DEBT',
]);
const nonNegativeMoney = moneySchema.refine((value) => compareDecimal(value, '0') >= 0, 'Must not be negative');
export const ledgerAccountCreateSchema = z.object({
    number: z
        .string()
        .trim()
        .regex(/^[A-Z0-9][A-Z0-9.-]{1,19}$/u),
    name: z.string().trim().min(1).max(160),
    kind: ledgerAccountKindSchema,
    role: ledgerAccountRoleSchema.default('GENERAL'),
    currency: currencyCodeSchema.nullable().default(null),
});
export const ledgerAccountSchema = ledgerAccountCreateSchema.extend({
    id: idSchema,
    active: z.boolean(),
    version: z.number().int(),
});
export const ledgerAccountUpdateSchema = ledgerAccountCreateSchema.extend({
    version: z.number().int().positive(),
    active: z.boolean(),
});
export const ledgerAccountListSchema = z.object({ data: z.array(ledgerAccountSchema) });
export const ledgerDimensionSchema = z.object({
    id: idSchema,
    code: z.string(),
    name: z.string(),
    kind: z.enum(['CENTER', 'OBJECT']),
    active: z.boolean(),
});
export const ledgerDimensionCreateSchema = ledgerDimensionSchema
    .omit({ id: true, active: true })
    .extend({ code: z.string().trim().min(1).max(80), name: z.string().trim().min(1).max(160) });
export const journalLineInputSchema = z
    .object({
    accountId: idSchema,
    debit: nonNegativeMoney.default('0'),
    credit: nonNegativeMoney.default('0'),
    description: z.string().trim().max(240).default(''),
    partnerId: idSchema.nullish(),
    documentId: idSchema.nullish(),
    vendorInvoiceId: idSchema.nullish(),
    costCenterId: idSchema.nullish(),
    costObjectId: idSchema.nullish(),
    taxCode: z.string().max(40).nullish(),
    originalCurrency: currencyCodeSchema.nullish(),
    originalAmount: moneySchema.nullish(),
    exchangeRate: z
        .string()
        .regex(/^\d+(\.\d{1,10})?$/u)
        .refine((v) => compareDecimal(v, '0') > 0)
        .nullish(),
})
    .superRefine((line, ctx) => {
    if (compareDecimal(line.debit, '0') > 0 && compareDecimal(line.credit, '0') > 0)
        ctx.addIssue({
            code: 'custom',
            path: ['credit'],
            message: 'A line has either debit or credit',
        });
    const fx = [line.originalCurrency, line.originalAmount, line.exchangeRate];
    if (fx.some((v) => v !== null && v !== undefined) &&
        !fx.every((v) => v !== null && v !== undefined))
        ctx.addIssue({
            code: 'custom',
            path: ['exchangeRate'],
            message: 'Currency, amount and rate are required together',
        });
});
export const journalCreateSchema = z.object({
    bookingDate: ledgerDateSchema,
    documentDate: ledgerDateSchema,
    serviceDate: ledgerDateSchema.nullish(),
    taxDate: ledgerDateSchema,
    description: z.string().trim().min(3).max(240),
    reference: z.string().trim().min(1).max(160),
    attachmentId: idSchema.nullish(),
    idempotencyKey: z.string().min(8).max(160),
    lines: z.array(journalLineInputSchema).min(2).max(500),
    post: z.boolean().default(false),
});
export const journalActionSchema = z.object({
    version: z.number().int().positive(),
    idempotencyKey: z.string().min(8).max(160),
});
export const journalUpdateSchema = journalCreateSchema.extend({
    version: z.number().int().positive(),
    post: z.literal(false).default(false),
});
export const journalReverseSchema = journalActionSchema.extend({
    bookingDate: ledgerDateSchema,
    reason: z.string().trim().min(10).max(2000),
});
export const journalLineSchema = z.object({
    id: idSchema,
    position: z.number().int(),
    accountId: idSchema,
    accountNumber: z.string(),
    accountName: z.string(),
    debit: moneySchema,
    credit: moneySchema,
    description: z.string(),
    partnerId: idSchema.nullable(),
    documentId: idSchema.nullable(),
    vendorInvoiceId: idSchema.nullable(),
    costCenterId: idSchema.nullable(),
    costObjectId: idSchema.nullable(),
    taxCode: z.string().nullable(),
    originalCurrency: currencyCodeSchema.nullable(),
    originalAmount: moneySchema.nullable(),
    exchangeRate: z.string().nullable(),
});
export const journalSchema = z.object({
    id: idSchema,
    number: z.number().int().nullable(),
    version: z.number().int(),
    status: z.enum(['DRAFT', 'POSTED']),
    withdrawn: z.boolean().optional(),
    bookingDate: ledgerDateSchema,
    documentDate: ledgerDateSchema,
    serviceDate: ledgerDateSchema.nullable(),
    taxDate: ledgerDateSchema,
    description: z.string(),
    reference: z.string(),
    currency: currencyCodeSchema,
    sourceType: z.string(),
    sourceId: z.string().nullable(),
    attachmentId: idSchema.nullable(),
    reversalOfId: idSchema.nullable(),
    reversalIds: z.array(idSchema),
    totalDebit: moneySchema,
    totalCredit: moneySchema,
    createdAt: isoDateTimeSchema,
    postedAt: isoDateTimeSchema.nullable(),
    createdById: idSchema,
    postedById: idSchema.nullable(),
    lines: z.array(journalLineSchema),
});
export const ledgerQuerySchema = z.object({
    costCenterId: idSchema.optional(),
    costObjectId: idSchema.optional(),
    journalId: idSchema.optional(),
    dateBasis: z.enum(['BOOKING', 'TAX']).optional(),
    dateFrom: ledgerDateSchema.optional(),
    dateTo: ledgerDateSchema.optional(),
    accountId: idSchema.optional(),
    partnerId: idSchema.optional(),
    search: z.string().max(160).optional(),
    status: z.enum(['DRAFT', 'POSTED']).optional(),
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(200).default(50),
});
export const journalListSchema = z.object({
    data: z.array(journalSchema),
    total: z.number().int(),
});
export const ledgerExportQuerySchema = ledgerQuerySchema.extend({
    locale: z.enum(SUPPORTED_LOCALES).default('de'),
});
export const fiscalYearCreateSchema = z
    .object({
    name: z.string().trim().min(1).max(80),
    dateFrom: ledgerDateSchema,
    dateTo: ledgerDateSchema,
})
    .refine((v) => {
    const anniversary = new Date(`${v.dateFrom}T00:00:00.000Z`);
    anniversary.setUTCFullYear(anniversary.getUTCFullYear() + 1);
    return v.dateFrom <= v.dateTo && new Date(`${v.dateTo}T00:00:00.000Z`) < anniversary;
}, { path: ['dateTo'], message: 'Fiscal years must contain at most twelve months' });
export const fiscalPeriodSchema = z.object({
    id: idSchema,
    yearId: idSchema,
    name: z.string(),
    dateFrom: ledgerDateSchema,
    dateTo: ledgerDateSchema,
    locked: z.boolean(),
    version: z.number().int(),
});
export const fiscalYearSchema = z.object({
    id: idSchema,
    name: z.string(),
    dateFrom: ledgerDateSchema,
    dateTo: ledgerDateSchema,
    closedAt: isoDateTimeSchema.nullable(),
    periods: z.array(fiscalPeriodSchema),
});
export const periodLockSchema = z.object({
    version: z.number().int().positive(),
    locked: z.boolean(),
    reason: z.string().trim().min(10).max(2000),
});
export const ledgerSettingsSchema = z.object({
    enabled: z.boolean(),
    startDate: ledgerDateSchema.nullable(),
    currency: currencyCodeSchema,
    vatMethod: z.enum(['ACCRUAL', 'CASH']),
    version: z.number().int(),
});
export const ledgerSetupSchema = z.object({
    startDate: ledgerDateSchema,
    currency: z.literal('EUR').default('EUR'),
    vatMethod: z.enum(['ACCRUAL', 'CASH']).default('ACCRUAL'),
});
export const ledgerWorkspaceSchema = z.object({
    businessDate: ledgerDateSchema,
    settings: ledgerSettingsSchema,
    accounts: z.array(ledgerAccountSchema),
    years: z.array(fiscalYearSchema),
    dimensions: z.array(ledgerDimensionSchema),
});
export const trialBalanceRowSchema = z.object({
    accountId: idSchema,
    number: z.string(),
    name: z.string(),
    kind: ledgerAccountKindSchema,
    role: ledgerAccountRoleSchema,
    opening: moneySchema,
    debit: moneySchema,
    credit: moneySchema,
    balance: moneySchema,
});
export const ledgerReportSchema = z.object({
    dateBasis: z.enum(['BOOKING', 'TAX']).optional(),
    dateFrom: ledgerDateSchema,
    dateTo: ledgerDateSchema,
    currency: currencyCodeSchema,
    rows: z.array(trialBalanceRowSchema),
    totalDebit: moneySchema,
    totalCredit: moneySchema,
    assets: moneySchema,
    liabilities: moneySchema,
    equity: moneySchema,
    revenue: moneySchema,
    expense: moneySchema,
    profit: moneySchema,
    balanceProfit: moneySchema,
    balanceDifference: moneySchema,
    balanced: z.boolean(),
});
//# sourceMappingURL=financial-accounting.js.map